import { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, X } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';
import { formatPhone, parsePhone } from '../utils/masks';
import { formatToSaoPaulo, nowInSaoPaulo } from '../utils/timezone';

interface ChatModalProps {
  vaga: {
    id: string;
    titulo: string;
  };
  isOpen: boolean;
  onClose: () => void;
}

interface Mensagem {
  id: string;
  texto: string;
  tipo: 'usuario' | 'ia';
  timestamp: string;
}

const ChatModal = ({ vaga, isOpen, onClose }: ChatModalProps) => {
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [inputMensagem, setInputMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [candidatoId, setCandidatoId] = useState<string | null>(null);
  const [etapaConversa, setEtapaConversa] = useState<'form' | 'qualificacao' | 'curriculo' | 'finalizada'>('form');
  const [dadosCandidato, setDadosCandidato] = useState({
    nome: '',
    email: '',
    telefone: ''
  });
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [displayPhone, setDisplayPhone] = useState('');
  const [configuracao, setConfiguracao] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    carregarConfiguracao();
  }, []);

  const carregarConfiguracao = async () => {
    try {
      const { data, error } = await supabase
        .from('configuracoes_ia_rh')
        .select('*')
        .eq('ativo', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) throw error;
      setConfiguracao(data);
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    }
  };

  useEffect(() => {
    if (isOpen && etapaConversa === 'qualificacao' && configuracao) {
      iniciarConversa();
    }
  }, [isOpen, etapaConversa, configuracao]);

  useEffect(() => {
    scrollToBottom();
  }, [mensagens]);

  const iniciarConversa = () => {
    if (!configuracao) return;
    
    const perguntas = configuracao.perguntas_qualificacao || [];
    const primeiraPergunta = perguntas.find((p: any) => p.ordem === 1);
    
    // Usar a mensagem inicial configurada e substituir as variáveis
    let mensagemTexto = configuracao.mensagem_inicial || 'Olá! 👋 Sou a assistente virtual. Vamos começar?';
    mensagemTexto = mensagemTexto.replace('{nome_assistente}', configuracao.nome_assistente || 'Sofia');
    mensagemTexto = mensagemTexto.replace('{vaga_titulo}', vaga.titulo);
    mensagemTexto = mensagemTexto.replace('{dadosCandidato.nome}', dadosCandidato.nome);
    
    // Adicionar a primeira pergunta com substituição de variáveis
    if (primeiraPergunta) {
      let perguntaTexto = primeiraPergunta.pergunta;
      perguntaTexto = perguntaTexto.replace('{dadosCandidato.nome}', dadosCandidato.nome);
      perguntaTexto = perguntaTexto.replace('{nome_candidato}', dadosCandidato.nome);
      mensagemTexto += `\n\n**${perguntaTexto}**`;
    }
    
    const mensagemInicial: Mensagem = {
      id: '1',
      texto: mensagemTexto,
      tipo: 'ia',
      timestamp: nowInSaoPaulo().toISOString()
    };
    setMensagens([mensagemInicial]);
    setEtapaConversa('qualificacao');
  };

  const gerarRespostaContextual = (perguntaAnterior: any, respostaUsuario: string): string => {
    const resposta = respostaUsuario.toLowerCase().trim();
    
    // Analisar o contexto da pergunta e a resposta do usuário
    const perguntaTexto = perguntaAnterior.pergunta.toLowerCase();
    
    // Respostas contextuais baseadas no conteúdo da pergunta e resposta
    
    // Para perguntas sobre experiência/qualificações
    if (perguntaTexto.includes('experiência') || perguntaTexto.includes('trabalhou')) {
      if (resposta.includes('sim') || resposta.includes('tenho') || /\d+\s*(ano|anos|mês|meses)/.test(resposta)) {
        return 'Que ótimo! Experiência é sempre um diferencial. 😊\n\n';
      } else {
        return 'Sem problemas! Todos começamos de algum lugar e estamos aqui para te ajudar a crescer. 💪\n\n';
      }
    }
    
    // Para perguntas sobre certificações (CRECI, etc.)
    if (perguntaTexto.includes('creci') || perguntaTexto.includes('certificação') || perguntaTexto.includes('licença')) {
      if (resposta.includes('sim') || resposta.includes('tenho') || resposta.includes('possuo')) {
        return 'Perfeito! Isso é uma grande vantagem para esta posição. ✅\n\n';
      } else if (resposta.includes('processo') || resposta.includes('andamento')) {
        return 'Que bom! Estar em processo já mostra seu comprometimento. 👍\n\n';
      } else {
        return 'Tudo bem! Podemos te orientar sobre como obter essa certificação. 📚\n\n';
      }
    }
    
    // Para perguntas sobre localização
    if (perguntaTexto.includes('cidade') || perguntaTexto.includes('mora') || perguntaTexto.includes('local')) {
      return 'Entendi! É importante conhecer sua localização para as oportunidades. 📍\n\n';
    }
    
    // Para perguntas sobre disponibilidade
    if (perguntaTexto.includes('disponibilidade') || perguntaTexto.includes('horário')) {
      return 'Perfeito! Essa informação nos ajuda a encontrar a melhor oportunidade para você. ⏰\n\n';
    }
    
    // Para perguntas sobre motivação/interesse
    if (perguntaTexto.includes('atrai') || perguntaTexto.includes('interesse') || perguntaTexto.includes('motiva')) {
      return 'Que resposta interessante! É ótimo ver sua motivação. 🌟\n\n';
    }
    
    // Resposta genérica positiva para outras situações
    return 'Obrigada pela informação! 👍\n\n';
  };

  const processarResposta = async (mensagemUsuario: string): Promise<string> => {
    if (!configuracao) return 'Erro: Configuração não carregada';
    
    const perguntas = configuracao.perguntas_qualificacao || [];
    const numMensagensUsuario = mensagens.filter(m => m.tipo === 'usuario').length;
    
    // Determinar qual é a próxima pergunta baseada no número de respostas já dadas
    const proximaOrdem = numMensagensUsuario + 2; // +2 porque começamos na pergunta 1 e o usuário já respondeu
    const proximaPergunta = perguntas.find((p: any) => p.ordem === proximaOrdem);
    
    // Se ainda há perguntas para fazer
    if (proximaPergunta) {
      // Gerar resposta contextual inteligente baseada na pergunta anterior e resposta do usuário
      let respostaContextual = '';
      
      const perguntaAnterior = perguntas.find((p: any) => p.ordem === proximaOrdem - 1);
      
      if (perguntaAnterior) {
        respostaContextual = gerarRespostaContextual(perguntaAnterior, mensagemUsuario);
      }
      
      // Formatear a próxima pergunta com substituição de variáveis
      let perguntaFormatada = proximaPergunta.pergunta;
      perguntaFormatada = perguntaFormatada.replace('{dadosCandidato.nome}', dadosCandidato.nome);
      perguntaFormatada = perguntaFormatada.replace('{nome_candidato}', dadosCandidato.nome);
      perguntaFormatada = `**${perguntaFormatada}**`;
      
      // Se for múltipla escolha, adicionar as opções
      if (proximaPergunta.tipo === 'multipla_escolha' && proximaPergunta.opcoes) {
        perguntaFormatada += '\n\nOpções:\n';
        proximaPergunta.opcoes.forEach((opcao: string, index: number) => {
          perguntaFormatada += `${index + 1}. ${opcao}\n`;
        });
      }
      
      return respostaContextual + perguntaFormatada;
    }
    
    // Se não há mais perguntas, usar a mensagem de finalização configurada
    setEtapaConversa('curriculo');
    return configuracao.mensagem_finalizacao || 'Perfeito! 🎉 Vou encaminhar seus dados para nossa equipe de RH. Eles entrarão em contato em até 24 horas para agendar a entrevista.';
  };

  const salvarCandidato = async () => {
    try {
      let curriculoUrl = '';
      
      // Upload do currículo se fornecido
      if (arquivo) {
        const fileExt = arquivo.name.split('.').pop();
        const fileName = `${Date.now()}-${dadosCandidato.nome.replace(/\s+/g, '-')}.${fileExt}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('curriculos')
          .upload(fileName, arquivo);

        if (uploadError) throw uploadError;
        curriculoUrl = uploadData.path;
      }

      // Inserir candidato
      const { data, error } = await supabase
        .from('candidatos_rh')
        .insert({
          nome: dadosCandidato.nome,
          email: dadosCandidato.email,
          telefone: dadosCandidato.telefone,
          curriculo_url: curriculoUrl || null,
          vaga_id: vaga.id,
          origem_lead: 'chat_ia',
          tipo_cadastro: 'chat_ia'
        })
        .select()
        .single();

      if (error) throw error;
      setCandidatoId(data.id);
      return data.id;
    } catch (error) {
      console.error('Erro ao salvar candidato:', error);
      return null;
    }
  };

  const salvarConversa = async (mensagem: string, tipo: 'usuario' | 'ia') => {
    try {
      await supabase.from('conversas_ia_rh').insert({
        candidato_id: candidatoId,
        mensagem,
        tipo,
        metadata: { vaga_id: vaga.id }
      });
    } catch (error) {
      console.error('Erro ao salvar conversa:', error);
    }
  };

  const handlePhoneChange = (value: string) => {
    const cleanPhone = parsePhone(value);
    setDisplayPhone(formatPhone(value));
    setDadosCandidato({...dadosCandidato, telefone: cleanPhone});
  };

  const iniciarFormulario = async () => {
    if (!dadosCandidato.nome || !dadosCandidato.email || !dadosCandidato.telefone) {
      alert('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    try {
      const novoId = await salvarCandidato();
      if (novoId) {
        setCandidatoId(novoId);
        iniciarConversa();
      } else {
        alert('Erro ao salvar dados. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao iniciar formulário:', error);
      alert('Erro ao iniciar conversa. Tente novamente.');
    }
  };


  const finalizarCurriculo = async () => {
    if (arquivo) {
      await salvarCandidato(); // Atualiza com o currículo
    }
    
    const mensagemFinal = arquivo 
      ? 'Muito obrigada! Seu currículo foi recebido com sucesso. Nossa equipe analisará seu perfil e entrará em contato em breve. 🎉'
      : 'Muito obrigada pelas informações! Nossa equipe analisará seu perfil e entrará em contato em breve. 🎉';
    
    const novaMensagemIA: Mensagem = {
      id: Date.now().toString(),
      texto: mensagemFinal,
      tipo: 'ia',
      timestamp: nowInSaoPaulo().toISOString()
    };

    setMensagens(prev => [...prev, novaMensagemIA]);
    await salvarConversa(mensagemFinal, 'ia');
    setEtapaConversa('finalizada');
  };

  const enviarMensagem = async () => {
    if (!inputMensagem.trim() || enviando) return;

    const novaMensagemUsuario: Mensagem = {
      id: Date.now().toString(),
      texto: inputMensagem,
      tipo: 'usuario',
      timestamp: nowInSaoPaulo().toISOString()
    };

    setMensagens(prev => [...prev, novaMensagemUsuario]);
    setEnviando(true);
    
    // Salvar mensagem do usuário
    await salvarConversa(inputMensagem, 'usuario');
    
    const mensagemTemp = inputMensagem;
    setInputMensagem('');

    // Simular delay da IA
    setTimeout(async () => {
      const respostaIA = await processarResposta(mensagemTemp);
      
      const novaMensagemIA: Mensagem = {
        id: (Date.now() + 1).toString(),
        texto: respostaIA,
        tipo: 'ia',
        timestamp: nowInSaoPaulo().toISOString()
      };

      setMensagens(prev => [...prev, novaMensagemIA]);
      await salvarConversa(respostaIA, 'ia');
      setEnviando(false);
      
      // Refocar o input após enviar a mensagem
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }, 1500);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviarMensagem();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-md h-[600px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center mr-3">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Sofia - IA</h3>
              <p className="text-xs text-gray-500">Assistente de RH</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {etapaConversa === 'form' && (
            <div className="flex items-start space-x-2">
              <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Bot className="h-3 w-3 text-white" />
              </div>
              <div className="bg-gray-100 text-gray-900 max-w-[80%] p-3 rounded-lg">
                <div 
                  className="text-sm whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ 
                    __html: (configuracao?.mensagem_inicial || 'Olá! 👋 Sou a assistente virtual. Vamos começar?')
                      .replace('{nome_assistente}', configuracao?.nome_assistente || 'Sofia')
                      .replace('{vaga_titulo}', `**${vaga.titulo}**`)
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                  }}
                />
              </div>
            </div>
          )}
          {mensagens.map((mensagem) => (
            <div
              key={mensagem.id}
              className={`flex items-start space-x-2 ${
                mensagem.tipo === 'usuario' ? 'justify-end' : 'justify-start'
              }`}
            >
              {mensagem.tipo === 'ia' && (
                <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Bot className="h-3 w-3 text-white" />
                </div>
              )}
              
              <div
                className={`max-w-[80%] p-3 rounded-lg ${
                  mensagem.tipo === 'usuario'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                <div 
                  className="text-sm whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ 
                    __html: mensagem.texto.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                  }}
                />
                <div className={`text-xs mt-1 ${
                  mensagem.tipo === 'usuario' ? 'text-orange-100' : 'text-gray-500'
                }`}>
                   {formatToSaoPaulo(mensagem.timestamp, 'HH:mm')}
                </div>
              </div>

              {mensagem.tipo === 'usuario' && (
                <div className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="h-3 w-3 text-white" />
                </div>
              )}
            </div>
          ))}
          
          {enviando && (
            <div className="flex items-start space-x-2">
              <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                <Bot className="h-3 w-3 text-white" />
              </div>
              <div className="bg-gray-100 p-3 rounded-lg">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Formulário inicial */}
        {etapaConversa === 'form' && (
          <div className="p-4 border-t border-gray-200">
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 mb-3">Para começarmos, preciso de algumas informações:</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  value={dadosCandidato.nome}
                  onChange={(e) => setDadosCandidato({...dadosCandidato, nome: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Seu nome completo"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-mail *
                </label>
                <input
                  type="email"
                  value={dadosCandidato.email}
                  onChange={(e) => setDadosCandidato({...dadosCandidato, email: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="seu@email.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone *
                </label>
                <input
                  type="tel"
                  value={displayPhone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="(11) 99999-9999"
                />
              </div>
              
              <button
                onClick={iniciarFormulario}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 px-4 rounded-lg transition-colors"
              >
                Iniciar Conversa
              </button>
            </div>
          </div>
        )}

        {/* Upload de currículo */}
        {etapaConversa === 'curriculo' && (
          <div className="p-4 border-t border-gray-200">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currículo (opcional)
                </label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setArquivo(e.target.files?.[0] || null)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Formatos aceitos: PDF, DOC, DOCX (máx. 10MB)
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={finalizarCurriculo}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Pular
                </button>
                <button
                  onClick={finalizarCurriculo}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-2 px-4 rounded-lg transition-colors"
                >
                  {arquivo ? 'Enviar Currículo' : 'Finalizar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Input de mensagem */}
        {etapaConversa === 'qualificacao' && (
          <div className="p-4 border-t border-gray-200">
            <div className="flex space-x-2">
              <input
                ref={inputRef}
                type="text"
                value={inputMensagem}
                onChange={(e) => setInputMensagem(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Digite sua resposta..."
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                disabled={enviando}
              />
              <button
                onClick={enviarMensagem}
                disabled={!inputMensagem.trim() || enviando}
                className="bg-orange-500 hover:bg-orange-600 text-white p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatModal;